import React, { useState, useEffect, useMemo, useRef, ReactElement } from 'react';
import { useDispatch } from 'react-redux';
import { Tooltip } from 'primereact/tooltip';
import Moment from 'moment';
import { FILE_STATUSES, DATE_FORMAT } from '@Constants/constants';
import { actions as filesActions } from '@Actions/filesActions';
import LoadingIndicator from '@Common/LoadingIndicator/LoadingIndicator';
import Notification from '@Common/Notification';
import LocationFileManagerDrawer from '@Common/LocationFileManagerDrawer/LocationFileManagerDrawer';
import { Modal61Ref } from '@Types/common';
import ImageGalleryModal from '../../../../TaskList/TaskDetail/LocationTaskSidebar/ImageGalleryModal';
import { LocationTaskFile } from '../../../../TaskList/TaskDetail/LocationTaskSidebar/Types';
import { useLoadLocationFiles } from '@Apis/locationDashboard';
import { LocationDetailData } from '../../types';
import './LocationDetailFilesTile.scss';

interface LocationDetailFilesTileProps {
    location: LocationDetailData;
    projectIds: number[];
}

const IMAGE_EXTENSIONS = new Set(['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp']);

// Strip a leading dot (API returns ".png", set stores "png") then lowercase.
const isImageFile = (extension: string) =>
    IMAGE_EXTENSIONS.has((extension || '').replace(/^\./, '').toLowerCase());

// Status icon + label per file status. Font Awesome equivalents of the mockup's
// primeicons: pi-clock, pi-check-circle, pi-ban.
const STATUS_CONFIG: Record<number, { icon: string; className: string; label: string }> = {
    [FILE_STATUSES.Pending]: { icon: 'fa-regular fa-clock', className: 'pending', label: 'Pending' },
    [FILE_STATUSES.Approved]: { icon: 'fa-regular fa-circle-check', className: 'approved', label: 'Approved' },
    [FILE_STATUSES.Denied]: { icon: 'fa-regular fa-ban', className: 'denied', label: 'Denied' },
};

const LocationDetailFilesTile = ({ location, projectIds: _projectIds }: LocationDetailFilesTileProps): ReactElement => {
    // dispatch kept only for the download side-effect (blob save via saveFile).
    // See plan note: filesActions.download is not cacheable server state;
    // migrating it adds little — leave as thin helper.
    const dispatch = useDispatch();

    const { data: files = [], isPending: isLoading, refetch: refetchFiles } = useLoadLocationFiles(
        location.locationKey
    );

    const [isFileManagerDrawerOpen, setIsFileManagerDrawerOpen] = useState<boolean>(false);
    const [selectedFileId, setSelectedFileId] = useState<string | null>(null);

    // Thumbnails: the load-files endpoint returns no preview URL, so image blobs are
    // downloaded client-side and converted to object URLs (same approach as FileCell.tsx).
    // Keyed by file id; object URLs are tracked separately so they can be revoked.
    const [imageUrls, setImageUrls] = useState<Record<number, string>>({});
    const objectUrlsRef = useRef<string[]>([]);

    // Gallery modal state — mirrors the LinksAndFiles.tsx pattern.
    const [activeImageIndex, setActiveImageIndex] = useState<{ index: number; opened: number }>({ index: -1, opened: 0 });
    const galleryModalRef = useRef<Modal61Ref>(null);

    // NOTE: the load-files endpoint (location-dashboard/load-files/{locationKey}) returns a minimal
    // file object that does NOT include projectId, so client-side project filtering is not possible
    // here — all files for the location are shown. The projectIds prop is kept in the signature for
    // future use if the backend ever enriches the endpoint to include projectId.

    // Download image blobs and build object URLs for thumbnails + the gallery. Runs whenever
    // the loaded file list changes (e.g. location change). Previous URLs are revoked first so
    // a reload does not leak. Thumbnails load async — the placeholder shows until each resolves.
    useEffect(() => {
        objectUrlsRef.current.forEach(URL.revokeObjectURL);
        objectUrlsRef.current = [];
        setImageUrls({});

        let cancelled = false;
        const imageFiles = files.filter(f => isImageFile(f.extension));
        imageFiles.forEach(async (f) => {
            try {
                const blob = await (dispatch(filesActions.download(f.fileName, false, false) as any) as any);
                if (cancelled || !blob) return;
                const url = URL.createObjectURL(blob);
                objectUrlsRef.current.push(url);
                setImageUrls(prev => ({ ...prev, [f.id]: url }));
            } catch {
                // Ignore individual thumbnail failures — the placeholder remains.
            }
        });

        return () => { cancelled = true; };
    }, [files]); // eslint-disable-line react-hooks/exhaustive-deps

    // Revoke every created object URL on unmount.
    useEffect(() => {
        return () => {
            objectUrlsRef.current.forEach(URL.revokeObjectURL);
            objectUrlsRef.current = [];
        };
    }, []);

    // Image files (within the current view) that have a resolved object URL — the gallery set.
    const galleryFiles = useMemo(
        () => files
            .filter(f => isImageFile(f.extension) && imageUrls[f.id])
            .map(f => ({ id: f.id, name: f.name, imageObjectURL: imageUrls[f.id] })),
        [files, imageUrls]
    );

    // Show the gallery once React commits a (re)opened active index.
    useEffect(() => {
        if (activeImageIndex.opened > 0 && activeImageIndex.index >= 0) {
            galleryModalRef.current?.show();
        }
    }, [activeImageIndex]);

    const onCardClick = async (file: typeof files[number]) => {
        // Image with a ready thumbnail → open the gallery at that image's index.
        // Everything else → download the file.
        if (isImageFile(file.extension) && imageUrls[file.id]) {
            const idx = galleryFiles.findIndex(f => f.id === file.id);
            setActiveImageIndex(prev => ({ index: idx < 0 ? 0 : idx, opened: prev.opened + 1 }));
            return;
        }
        try {
            await (dispatch(filesActions.download(file.fileName) as any) as any);
        } catch (error) {
            Notification('error', 'Error', error);
        }
    };

    const onEditClick = (e: React.MouseEvent, file: typeof files[number]) => {
        e.stopPropagation(); // prevent card click (download) from firing
        setSelectedFileId(file.id.toString());
        setIsFileManagerDrawerOpen(true);
    };

    const onAddFile = () => {
        setSelectedFileId(null);
        setIsFileManagerDrawerOpen(true);
    };

    const onFileManagerCancel = () => {
        setIsFileManagerDrawerOpen(false);
    };

    const onFileManagerComplete = () => {
        setIsFileManagerDrawerOpen(false);
        refetchFiles();
    };

    const formatDate = (uploadDate?: Date | null): string => {
        if (!uploadDate) return '';
        const m = Moment.utc(uploadDate);
        return `${m.format(DATE_FORMAT)} · ${m.fromNow()}`;
    };

    const renderStatusIcon = (file: typeof files[number]) => {
        const status = STATUS_CONFIG[file.fileStatusId];
        if (!status) return null;

        // Tooltip target is per-file so each icon carries its own hover label.
        const tooltipTarget = `file-status-icon-${file.id}`;
        return (
            <>
                <Tooltip target={`.${tooltipTarget}`} content={status.label} position='right' />
                <span className={`file-status-icon ${status.className} ${tooltipTarget}`}>
                    <i className={status.icon} aria-hidden='true' />
                     <span className='sr-only'>{status.label}</span>
                </span>
            </>
        );
    };

    return (
        <div className='location-detail-files-tile'>
            <LoadingIndicator isLoading={isLoading}>
                <div className='file-cards'>
                    {files.map(file => (
                        <div
                            key={file.id}
                            className='file-card'
                            onClick={() => onCardClick(file)}
                            role='button'
                            tabIndex={0}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') onCardClick(file);
                                if (e.key === ' ') { e.preventDefault(); onCardClick(file); }
                            }}
                        >
                            {/* Only image files render a preview thumbnail; document files keep the
                                text-first layout from the mockup (no generic file placeholder tile). */}
                            {isImageFile(file.extension) && (
                                <div className='file-thumb'>
                                    {imageUrls[file.id] ? (
                                        <img className='file-thumb-img' src={imageUrls[file.id]} alt={file.name} />
                                    ) : (
                                        <span className='file-thumb-placeholder'>
                                            <i className='fa-regular fa-image' aria-hidden='true' />
                                        </span>
                                    )}
                                </div>
                            )}

                            <div className='file-card-body'>
                                <div className='file-name'>{file.name}</div>
                                {file.description && (
                                    <div className='file-description'>{file.description}</div>
                                )}

                                <div className='file-meta'>
                                    {file.disciplineName && (
                                        <div className='file-meta-row'>
                                            <i className='fa-regular fa-building' aria-hidden='true' />
                                            <span>{file.disciplineName}</span>
                                        </div>
                                    )}

                                    {file.projectName && (
                                        <div className='file-meta-row'>
                                            {/* fa-folder (U+F07B) is not in the trimmed fa-regular
                                                subset — folder-closed is, and matches the mockup. */}
                                            <i className='fa-regular fa-folder-closed' aria-hidden='true' />
                                            <span>{file.projectName}</span>
                                        </div>
                                    )}

                                    {file.uploadDate && (
                                        <div className='file-meta-row date'>
                                            <i className='fa-regular fa-calendar' aria-hidden='true' />
                                            <span>{formatDate(file.uploadDate)}</span>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className='file-card-actions'>
                                <button
                                    type='button'
                                    className='file-card-edit'
                                    title='Edit file'
                                    onClick={e => onEditClick(e, file)}
                                    aria-label='Edit file'
                                >
                                    <i className='fa-regular fa-pen' />
                                </button>

                                {renderStatusIcon(file)}
                            </div>
                        </div>
                    ))}

                    {!isLoading && files.length === 0 && (
                        <div className='files-empty'>No files yet.</div>
                    )}
                </div>

                <div className='files-footer'>
                    <span className='files-count'>
                        {files.length} file{files.length === 1 ? '' : 's'}
                    </span>
                    <button type='button' className='add-file-btn' onClick={onAddFile}>
                        <i className='fa-regular fa-upload' />
                        <span>Add File</span>
                    </button>
                </div>
            </LoadingIndicator>

            {isFileManagerDrawerOpen && (
                <LocationFileManagerDrawer
                    onComplete={onFileManagerComplete}
                    onCancel={onFileManagerCancel}
                    locationKey={location.locationKey}
                    fileId={selectedFileId}
                />
            )}

            <ImageGalleryModal
                modalRef={galleryModalRef}
                files={galleryFiles as unknown as LocationTaskFile[]}
                initialIndex={activeImageIndex.index}
            />
        </div>
    );
};

export default LocationDetailFilesTile;
